import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import { emptyMap, messages } from '../constants';
import * as D from '../deps';

import AlbumGridItemThirdLine from './common/AlbumGridItemThirdLine';

const {
  GridItem,
  getBackstagePathForToken,
  getArt,
  PandoraTypes,
  getInDomain,
  getSourceCardMenuItems,
  isRadioOnly,
  isUnavailable,
  getBackstageLocation,
} = D;

export class BackstageAlbumGridItem extends React.Component {
  static propTypes = {
    pandoraId: PropTypes.string,
    musicId: PropTypes.string,
    albumSeoToken: PropTypes.string,
    albumBackstagePath: PropTypes.string,
    artUrl: PropTypes.string,
    albumTitle: PropTypes.string,
    trackCount: PropTypes.number,
    year: PropTypes.string,
    isPlaceholder: PropTypes.bool,
    dominantColor: PropTypes.string,
    isLarge: PropTypes.bool,
    pauseTrack: PropTypes.func,
    playResumeSource: PropTypes.func,
    isDisabled: PropTypes.bool,
    isCollected: PropTypes.bool,
    isRadioOnly: PropTypes.bool,
    isUnavailable: PropTypes.bool,
    isClean: PropTypes.bool,
    isExplicit: PropTypes.bool,
    isMobileWeb: PropTypes.bool,
    onDemandIsEnabled: PropTypes.bool,
    isAlbumPlaying: PropTypes.bool,
    isAlbumPaused: PropTypes.bool,
    sourceCardMenuItems: PropTypes.array,
    showPremiumAccess: PropTypes.bool,
    isFluid: PropTypes.bool,
    twoColumns: PropTypes.bool,
    collaborationName: PropTypes.string,
    collaborationUrl: PropTypes.string,
  };

  static contextType = D.AppContext;

  onPlayButtonClick = () => {
    this.props.playResumeSource({
      id: this.props.pandoraId,
      pandoraType: null,
      forceRestartSourceIfPlaying: this.props.showPremiumAccess,
      context: D.Constants.Ads.Rewards.ContextTypes.BACKSTAGE,
      shouldShuffle: false,
      pageUrl: this.props.albumBackstagePath,
    });
  };

  onPauseButtonClick = () => {
    if (this.props.onDemandIsEnabled) {
      this.props.pauseTrack();
    }
  };

  additionalIntegrationContext = () => {
    return { pandoraId: this.props.pandoraId };
  };

  getBadge = () => {
    const { isClean, isExplicit } = this.props;

    if (isClean) {
      return <D.Badge badgeType={D.BadgeTypes.CLEAN} />;
    } else if (isExplicit) {
      return <D.Badge badgeType={D.BadgeTypes.EXPLICIT_SHORT} />;
    }

    return null;
  };

  render() {
    const albumBackstagePath = this.props.albumSeoToken
      ? getBackstagePathForToken(this.props.albumSeoToken)
      : getBackstageLocation({
          path: this.props.albumBackstagePath,
          pandoraId: this.props.pandoraId,
        });

    return (
      <D.AppContextIntegrationData data={this.additionalIntegrationContext}>
        <GridItem
          imgUrl={this.props.artUrl}
          itemOnclickUrl={albumBackstagePath}
          mainText={this.props.albumTitle}
          mainTextUrl={albumBackstagePath}
          secondText={
            <FormattedMessage
              {...messages.totalSongs}
              values={{ trackCount: this.props.trackCount }}
            />
          }
          thirdText={
            <AlbumGridItemThirdLine
              preText={this.props.year}
              mainText={this.props.collaborationName}
              mainTextUrl={this.props.collaborationUrl}
              Badge={this.getBadge()}
            />
          }
          isPlaceholder={this.props.isPlaceholder}
          isPlaying={this.props.isAlbumPlaying}
          isPaused={this.props.isAlbumPaused}
          sourceCardMenuItems={this.props.sourceCardMenuItems}
          dominantColor={this.props.dominantColor}
          playButtonOnclick={this.onPlayButtonClick}
          pauseButtonOnclick={this.onPauseButtonClick}
          isDisabled={
            (!this.props.onDemandIsEnabled && !this.props.showPremiumAccess) ||
            this.props.isRadioOnly
          }
          isCollected={this.props.isCollected}
          isRadioOnly={this.props.isRadioOnly}
          isUnavailable={this.props.isUnavailable}
          isLarge={this.props.isLarge}
          isOnlyArt={this.props.isMobileWeb}
          twoColumns={this.props.twoColumns}
          isFluid={this.props.isFluid}
        />
      </D.AppContextIntegrationData>
    );
  }
}

function getArtistCollaborationProps(state, annotation = emptyMap) {
  const isCollaboration = annotation.get('collaboration');

  return {
    collaborationName: isCollaboration ? annotation.get('name') : null,
    collaborationUrl: isCollaboration ? annotation.get('shareableUrlPath') : null,
  };
}

export function mapStateToProps(state, ownProps) {
  const getAnnotations = getInDomain('catalog.utils.getAnnotations');
  const isAlbumPlayingCurrently = getInDomain('playback.utils.isSourcePlayingCurrently', false);
  const isPandoraIdInCollection = getInDomain('collection.utils.isPandoraIdInCollection', false);
  const isClean = getInDomain('catalog.utils.isClean');
  const isExplicit = getInDomain('catalog.utils.isExplicit');
  const isAudioPlaying = getInDomain('playback.utils.isAudioPlaying', false);

  const pandoraId = ownProps.pandoraId;
  const props = {};

  const albumAnnotations = getAnnotations(state, pandoraId);
  if (albumAnnotations && !albumAnnotations.isEmpty()) {
    const audioIsPlaying = isAudioPlaying(state);
    const isAlbumActive = isAlbumPlayingCurrently(state, pandoraId);
    const artistId = albumAnnotations.get('artistId');
    const artistAnnotations = getAnnotations(state, artistId);
    const { collaborationName, collaborationUrl } = getArtistCollaborationProps(
      state,
      artistAnnotations,
    );

    props.collaborationName = collaborationName;
    props.collaborationUrl = collaborationUrl;
    props.onDemandIsEnabled = D.Utils.Listener.hasPremiumFeatures(state);
    props.albumBackstagePath = albumAnnotations.get('shareableUrlPath');
    props.artUrl = getArt(albumAnnotations, 500, state);
    props.albumTitle = albumAnnotations.get('name');
    props.trackCount = albumAnnotations.get('trackCount');
    props.isAlbumPlaying = isAlbumActive && audioIsPlaying;
    props.isAlbumPaused = isAlbumActive && !audioIsPlaying;
    props.isRadioOnly = isRadioOnly(albumAnnotations);
    props.isUnavailable = isUnavailable(albumAnnotations);
    props.isClean = isClean(albumAnnotations);
    props.isExplicit = isExplicit(albumAnnotations);

    if (albumAnnotations.get('releaseDate')) {
      props.year = new Date(albumAnnotations.get('releaseDate')).getUTCFullYear().toString();
    }

    props.isCollected = isPandoraIdInCollection(state, pandoraId);
    props.sourceCardMenuItems = getSourceCardMenuItems({
      state,
      pandoraId,
      pandoraType: PandoraTypes.ALBUM,
      shareParentSource: {
        shareParentSourceId: pandoraId,
        shareParentSourceType: PandoraTypes.ALBUM,
      },
    });

    props.dominantColor = D.Utils.Art.getDominantColor(albumAnnotations);
  } else {
    props.isPlaceholder = true;
  }

  props.showPremiumAccess = D.Utils.Listener.showPremiumAccess(state);
  props.isMobileWeb = D.isMobileWeb(state);

  return props;
}

const mapDispatchToProps = {
  pauseTrack: D.Actions.UseCases.pauseTrack,
  playResumeSource: D.playResumeSource,
};

export default connect(mapStateToProps, mapDispatchToProps)(BackstageAlbumGridItem);
